/**
 * v0.9.1734: treat an UNREACHABLE image as no image.
 *
 * Menu photos used to live in a Google Cloud bucket whose project was lost
 * with the account that owned it. Those objects now refuse every request,
 * so the URLs in the database point at nothing - but they are still URLs,
 * so every screen dutifully renders an <img> that fails.
 *
 * The visible result is a broken thumbnail, which is worse than an empty
 * slot in the way that matters: an empty slot says "this item needs a
 * picture" and gets fixed, while a broken one looks like a temporary
 * loading glitch and gets scrolled past. With a hundred items to
 * regenerate, the difference is whether the list can be used as a worklist.
 *
 * Clearing the column would also work and is the tidier end state, but it
 * is a destructive one-way edit across several tables, and this is one
 * function that makes every surface agree in the meantime.
 */

/**
 * Hosts whose images can no longer be fetched.
 *
 * Deliberately a list of DEAD hosts rather than a list of allowed ones. An
 * allowlist would hide any image served from somewhere nobody thought of -
 * a customer's own upload, a future provider - and silently blanking a
 * working photo is a worse failure than showing a broken one.
 */
const DEAD_IMAGE_HOSTS = [
  'storage.googleapis.com',
];

/**
 * The URL to render, or null if there is nothing usable.
 *
 * Accepts anything the callers hold: a URL, a data: URI, null, undefined,
 * or the empty string that a cleared field leaves behind.
 */
export function usablePhotoUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const u = url.trim();
  if (!u) return null;
  // A data: URI is self-contained and always renders - these are the older
  // base64 photos, which survived the bucket entirely.
  if (u.startsWith('data:')) return u;
  for (const host of DEAD_IMAGE_HOSTS) {
    if (u.includes(host)) return null;
  }
  return u;
}

/** True when an item still needs a picture. */
export function needsPhoto(url: string | null | undefined): boolean {
  return usablePhotoUrl(url) === null;
}
